import React, {
  createContext,
  type FC,
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import CookieType from "@constants/CookieType";
import { useLocation } from "@gatsbyjs/reach-router";
import type { EligibleAffiliate } from "@interfaces/LandingPricing";
import {
  useConfigurationState,
  useConfigurationStore,
} from "@stores/configuration";
import {
  findAffiliateByCode,
  injectAffiliatePrefix,
  parseAffiliateFromPath,
} from "@utilities/affiliate";
import axios from "@utilities/axios";
import Cookies from "js-cookie";

// A referral the store lost (other subdomain, cleared storage) comes back from
// the affiliate cookie that updateAffiliate() writes. GTM also writes that
// cookie from any `?a=`, so it is validated like a link before it is used.
const affiliateFromCookie = (): string => {
  const brandCode = (process.env.GATSBY_AFFILIATE_CODE ?? "").toUpperCase();
  const storeCode = (
    useConfigurationStore.getState().affiliate ?? ""
  ).toUpperCase();
  const cookieCode = (Cookies.get(CookieType.AFFILIATE) ?? "").toUpperCase();
  if (
    !useConfigurationStore.persist.hasHydrated() ||
    storeCode !== brandCode ||
    cookieCode === brandCode
  ) {
    return "";
  }
  return cookieCode;
};

interface AffiliateContextValue {
  affiliate: EligibleAffiliate | null;
  isResolvingAffiliate: boolean;
}

const AffiliateContext = createContext<AffiliateContextValue>({
  affiliate: null,
  isResolvingAffiliate: false,
});

export const AffiliateProvider: FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { pathname } = useLocation();
  const { affiliate: affiliateCode, affiliateMeta } = useConfigurationState();
  // Start `true` so the SSR HTML — which Gatsby builds without query strings
  // — renders a skeleton instead of the BNESIM logo. The mount effect below
  // flips it back to `false` immediately if there is no `?a=CODE` validation
  // in flight; otherwise it stays `true` until the POST resolves. Net effect:
  // every visitor sees a brief skeleton at first paint, then the right logo
  // (BNESIM / affiliate) with no jarring swap.
  const [isValidatingParam, setIsValidatingParam] = useState<boolean>(true);

  // `?a=CODE` query-param validation. The server has to authorise the code
  // before we render its branding, so the skeleton stays up during the
  // round-trip. If there's no `?a=` (or the store already has the same code
  // from a cookie), we drop the skeleton immediately.
  useEffect(() => {
    if (typeof window === "undefined") {
      setIsValidatingParam(false);
      return;
    }
    const checkAffiliateCode = (code: string): void => {
      const currentStoreCode = useConfigurationStore.getState().affiliate;
      if (currentStoreCode?.toUpperCase() === code) {
        setIsValidatingParam(false);
        return;
      }

      const formData = new FormData();
      formData.append(
        "affiliate_code",
        process.env.GATSBY_AFFILIATE_CODE ?? "",
      );
      formData.append("sub_affiliate_code", code);
      void axios
        .post("/affiliate_code_check/", formData)
        .then(response => {
          if (response.data?.status === true) {
            const store = useConfigurationStore.getState();
            store.updateAffiliate(code);
            // The API returns the full affiliate object (or null) for runtime
            // branding. JSON-listed affiliates still win in the lookup below,
            // so storing this meta is purely the source for API-only affiliates
            // that aren't in `eligible_affiliates`.
            const apiAffiliate: EligibleAffiliate | null =
              response.data?.eligible_affiliate ?? null;
            store.setAffiliateMeta(apiAffiliate);
          }
        })
        .finally(() => {
          setIsValidatingParam(false);
        });
    };

    const params = new URLSearchParams(window.location.search);
    const paramCode = (params.get("a") ?? "").toUpperCase();
    if (paramCode !== "") {
      checkAffiliateCode(paramCode);
      return;
    }
    setIsValidatingParam(false);
    // No `?a=`: once Layout has loaded the store, take back a referral it
    // lost from the affiliate cookie, checked the same way.
    const restoreFromCookie = (): void => {
      const cookieCode = affiliateFromCookie();
      if (cookieCode !== "") {
        checkAffiliateCode(cookieCode);
      }
    };
    if (useConfigurationStore.persist.hasHydrated()) {
      restoreFromCookie();
      return;
    }
    return useConfigurationStore.persist.onFinishHydration(restoreFromCookie);
  }, []);

  // Two-way sync between the URL and the stored affiliate:
  //   1. If the URL already carries an affiliate segment, push it into the
  //      store so header/footer branding picks up the affiliate.
  //   2. If the store holds an eligible affiliate but the current URL doesn't
  //      show its prefix (e.g. after a clean-URL <Link> click or a ?a=CODE
  //      landing), mirror the prefix into the address bar via replaceState.
  //      Gatsby's router state stays on the served page, so this is purely
  //      cosmetic — no route transition, no re-render loop.
  useEffect(() => {
    const fromUrl = parseAffiliateFromPath(pathname);
    if (fromUrl !== null) {
      const store = useConfigurationStore.getState();
      if (store.affiliate?.toUpperCase() !== fromUrl.code.toUpperCase()) {
        store.updateAffiliate(fromUrl.code);
      }
    }
    if (typeof window === "undefined") return;
    // Only JSON-listed affiliates have a build-time route; mirroring a
    // prefix into the URL bar for an API-only affiliate would point at a
    // path Gatsby never built, so direct hits / refreshes would 404.
    const active = findAffiliateByCode(affiliateCode);
    if (active === null || active.url_prefix === "") return;
    // Read the actual address bar rather than the reach-router pathname —
    // the two drift out of sync after replaceState (which does not notify
    // the router), so comparing against reach-router's state can re-trigger
    // the rewrite on every subsequent render and snowball extra segments.
    const { pathname: current, search, hash } = window.location;
    const wanted = injectAffiliatePrefix(current, active.url_prefix);
    if (wanted !== current) {
      window.history.replaceState(null, "", wanted + search + hash);
    }
  }, [pathname, affiliateCode]);

  const value = useMemo<AffiliateContextValue>(() => {
    // Prefer the JSON entry (authoritative for URL-prefix-eligible affiliates
    // that `gatsby-node.ts` registers routes for). Fall back to the API-cached
    // metadata for runtime-only affiliates added through admin approval — but
    // only when its code still matches the stored code, so a stale meta from
    // a different past `?a=` landing can't leak branding through.
    const fromJson = findAffiliateByCode(affiliateCode);
    const fromApi =
      affiliateMeta !== null &&
      affiliateMeta.code.toUpperCase() === affiliateCode.toUpperCase()
        ? affiliateMeta
        : null;
    return {
      affiliate: fromJson ?? fromApi,
      isResolvingAffiliate: isValidatingParam,
    };
  }, [affiliateCode, affiliateMeta, isValidatingParam]);

  return (
    <AffiliateContext.Provider value={value}>
      {children}
    </AffiliateContext.Provider>
  );
};

export const useAffiliate = (): AffiliateContextValue =>
  useContext(AffiliateContext);
